if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Check inventory before submitting
        if (!this.validateInventoryBeforeSubmit()) {
          return; // Don't submit if inventory check fails
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          // Check if we should skip cart notification due to inventory limits
          const canAddToCart = this.validateInventoryBeforeSubmit();
          let sectionsToRender = this.cart.getSectionsToRender();
          
          if (!canAddToCart) {
            // Remove cart notification sections if inventory limit reached
            sectionsToRender = sectionsToRender.filter(section => 
              !['cart-notification-product', 'cart-notification-button'].includes(section.id)
            );
          }
          
          formData.append(
            'sections',
            sectionsToRender.map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              // Check if we should show notification based on inventory
              const canAddToCart = this.validateInventoryBeforeSubmit();
              
              // Always publish the PUB_SUB_EVENTS.cartUpdate for other components
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
              
              if (canAddToCart) {
                // Normal cart update - let cart notification show
                document.dispatchEvent(new CustomEvent('cart:updated', {
                  detail: response
                }));
              } else {
                // Show the inventory limit message instead of cart notification
                // Note: validateInventoryBeforeSubmit already shows the error message
                
                // Dispatch cart:updated but with suppression flag
                document.dispatchEvent(new CustomEvent('cart:updated', {
                  detail: {
                    ...response,
                    suppressNotification: true
                  }
                }));
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      validateInventoryBeforeSubmit() {
        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const requestedQuantity = parseInt(formData.get('quantity') || '1');
        
        if (!variantId) return true; // No variant selected, let normal validation handle it
        
        // Get current variant data from the page
        const variantData = this.getVariantData(variantId);
        if (!variantData) return true; // Can't find variant data, allow submission
        
        // If inventory is not managed by Shopify or allows continuing when out of stock, allow submission
        if (variantData.inventory_management !== 'shopify' || variantData.inventory_policy === 'continue') {
          return true;
        }
        
        // Calculate current quantity in cart for this variant
        const currentQuantityInCart = this.getCurrentCartQuantityForVariant(variantId);
        const totalRequestedQuantity = currentQuantityInCart + requestedQuantity;
        
        // Check if total would exceed available inventory
        if (totalRequestedQuantity > variantData.inventory_quantity) {
          const maxCanAdd = variantData.inventory_quantity - currentQuantityInCart;
          
          if (maxCanAdd <= 0) {
            // Show error message - no more can be added
            this.showInventoryError(`Maximum quantity already in cart.`);
          } else {
            // Show error message - can only add X more
            this.showInventoryError(`Only ${maxCanAdd} more can be added to cart.`);
          }
          
          return false;
        }
        
        return true;
      }
      
      getVariantData(variantId) {
        // Try to get variant data from window.variantData or product JSON
        if (window.product && window.product.variants) {
          return window.product.variants.find(v => v.id == variantId);
        }
        
        // Fallback: try to get from data attributes or other sources
        const variantInput = this.form.querySelector(`[name="id"][value="${variantId}"]`);
        if (variantInput) {
          const container = variantInput.closest('[data-variant-inventory]');
          if (container) {
            return {
              id: variantId,
              inventory_quantity: parseInt(container.dataset.variantInventory || '0'),
              inventory_management: container.dataset.inventoryManagement || 'shopify',
              inventory_policy: container.dataset.inventoryPolicy || 'deny'
            };
          }
        }
        
        return null;
      }
      
      getCurrentCartQuantityForVariant(variantId) {
        // Try to get from cart data if available
        if (window.cart && window.cart.items) {
          return window.cart.items
            .filter(item => item.variant_id == variantId)
            .reduce((sum, item) => sum + item.quantity, 0);
        }
        
        // Fallback: count from cart drawer or notification if visible
        const cartItems = document.querySelectorAll('[data-variant-id="' + variantId + '"] .quantity-select');
        let total = 0;
        cartItems.forEach(select => {
          total += parseInt(select.value || '0');
        });
        
        return total;
      }
      
      showInventoryError(message) {
        // Show error message to user
        alert(message);
        
        // Reset submit button
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
